import { Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { map, share, tap } from 'rxjs/operators';

import { GlobalSettingsVM } from "../models/GlobalSettingsVM";
import { CountryVM } from "../models/CountryVM";
import { NationalityVM } from "../models/NationalityVM";
import { CurrencyVM } from "../models/CurrencyVM";
import { LanguageVM } from "../models/LanguageVM";
import { CounsellingTopicVM } from "../models/CounsellingTopicVM";
import { ReasonForMedicationVM } from "../models/ReasonForMedicationVM";
import { CalendarSlotVM } from "../models";
import { TimeZoneInfoVM } from "../models/TimeZoneInfoVM";
import { CounsellorWorkingHoursVM } from "../models/CounsellorWorkingHoursVM";
import { HttpClient, HttpHeaders } from "@angular/common/http";

@Injectable()
export class ReferenceDataService {

  private globalSettings: GlobalSettingsVM;
  private countries: CountryVM[];
  private states: string[];
  private nationalities: NationalityVM[];
  private currencies: CurrencyVM[];
  private languages: LanguageVM[];
  private slotTimes: CounsellorWorkingHoursVM[];
  private timeZones: TimeZoneInfoVM[];

  constructor(private http: HttpClient) {

  }

  public getGlobalSettings(): Observable<GlobalSettingsVM> {
    if (this.globalSettings) return of(this.globalSettings);

    return this.http.get('/api/reference/settings', this.getHttpOptions(false)).pipe(
      map(resp => resp as GlobalSettingsVM),
      tap(settings => {
        this.globalSettings = settings;
      }),
      share(),);
  }

  public getCountries(enabledOnly: boolean): Observable<CountryVM[]> {
    if (this.countries) return of(this.countries);

    return this.http.get('/api/reference/countries/' + enabledOnly.toString(), this.getHttpOptions(false)).pipe(
      map((resp) => resp as CountryVM[]),
      tap(countries => {
        this.countries = countries;
      }),
      share(),);
  }

  public getStates(): Observable<string[]> {
    if (this.states) return of(this.states);

    return this.http.get('/api/reference/states/', this.getHttpOptions(false)).pipe(
      map((resp) => resp as string[]),
      tap(states => {
        this.states = states;
      }),
      share(),);
  }

  public getCurrencies(): Observable<CurrencyVM[]> {
    if (this.currencies) return of(this.currencies);

    return this.http.get('/api/reference/currencies', this.getHttpOptions(false)).pipe(
      map((resp) => resp as CurrencyVM[]),
      tap(currencies => {
        this.currencies = currencies;
      }),
      share(),);
  }

  public getLanguages(): Observable<LanguageVM[]> {
    if (this.languages) return of(this.languages);

    return this.http.get('/api/reference/languages', this.getHttpOptions(false)).pipe(
      map((resp) => resp as LanguageVM[]),
      tap(languages => {
        this.languages = languages;
      }),
      share(),);
  }

  public getNationalities(): Observable<NationalityVM[]> {
    if (this.nationalities) return of(this.nationalities);

    return this.http.get('/api/reference/nationalities', this.getHttpOptions(false)).pipe(
      map((resp) => resp as NationalityVM[]),
      tap(nationalities => {
        this.nationalities = nationalities;
      }),
      share(),);
  }

  public getCounsellingTopics(): Observable<CounsellingTopicVM[]> {
    return this.http.get('/api/reference/counsellingtopics', this.getHttpOptions(false)).pipe(
      map((resp) => resp as CounsellingTopicVM[]));
  }

  public getMedicationReasons(): Observable<ReasonForMedicationVM[]> {
    return this.http.get('/api/reference/reasonsformedication', this.getHttpOptions(false)).pipe(
      map((resp) => resp as ReasonForMedicationVM[]));
  }

  public getSlotTimes(): Observable<CounsellorWorkingHoursVM[]> {
    if (this.slotTimes) return of(this.slotTimes);

    return this.http.get('/api/reference/slot-times', this.getHttpOptions(false)).pipe(
      map((resp) => resp as CounsellorWorkingHoursVM[]),
      tap(times => {
        this.slotTimes = times;
      }),
      share());
  }


  public getTimeZones(): Observable<TimeZoneInfoVM[]> {
    if (this.timeZones) return of(this.timeZones);

    return this.http.get('/api/reference/timezones', this.getHttpOptions(false)).pipe(
      map((resp) => resp as TimeZoneInfoVM[]),
      tap(zones => {
        this.timeZones = zones;
      }),
      share());
  }

  protected getHttpOptions (authenticated: boolean, params?: URLSearchParams) {
    if (authenticated) {
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + localStorage.getItem('authToken'),
        });
        return { headers };
    }
    else {
        let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        return (params != null) ? { headers, search: params } : { headers };
    }
}

}
